import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useTimer from '../hooks/useTimer';
import useAntiCheat from '../hooks/useAntiCheat';
import useProctoring, { MAX_CONSECUTIVE_NO_FACE } from '../hooks/useProctoring';
import useHeartbeat from '../hooks/useHeartbeat';
import { fetchExam, checkDuplicate } from '../api/examService';
import { submitExam, startExamAttempt } from '../api/submissionService';
import { recordEvent, recordAnswerChange, recordExamSubmit, flushLogs } from '../utils/logger';

const ExamPortal = ({ studentName, preExamSessionId }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const questionPanelRef = useRef(null);
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showNoFaceWarning, setShowNoFaceWarning] = useState(false);
  const [faceBlocked, setFaceBlocked] = useState(false);
  const [faceVerified, setFaceVerified] = useState(false);
  const [faceReady, setFaceReady] = useState(false);
  const [blockMessage, setBlockMessage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const QUESTIONS_PER_PAGE = 5;
  const answersRef = useRef({});
  const submittedRef = useRef(false);
  const submittingRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const faceReadyTimerRef = useRef(null);

  const { examId } = useParams();

  const handleExpire = useCallback(() => {
    if (!submittedRef.current && handleSubmitRef.current) handleSubmitRef.current(true);
  }, []);

  const timer = useTimer(null, handleExpire);

  const {
    violations,
    violationsRef,
    faceDetected,
    gazeAway,
    recording,
    camError,
    eyesVisible,
    uploadSessionId,
    getUploadQueue,
    stopWebcam,
    retryWebcam,
    logViolation
  } = useProctoring(videoRef, canvasRef, examId, studentName, !submitted, audioContextRef);

  useAntiCheat({ logViolation });

  const { forceHeartbeat } = useHeartbeat(examId, studentName, !submitted, () => ({
    faceDetected,
    eyesVisible,
    gazeAway,
    answers: answersRef.current,
    violations: violationsRef.current
  }));

  // Require a face to be steadily visible before the exam can start
  useEffect(() => {
    if (submitted || faceVerified) return;
    if (recording && faceDetected && !camError) {
      if (!faceReadyTimerRef.current) {
        faceReadyTimerRef.current = setTimeout(() => setFaceReady(true), 1200);
      }
    } else {
      setFaceReady(false);
      clearTimeout(faceReadyTimerRef.current);
      faceReadyTimerRef.current = null;
    }
    return () => clearTimeout(faceReadyTimerRef.current);
  }, [recording, faceDetected, camError, submitted, faceVerified]);

  // DevTools detection (dimension check + element.toString trap)
  useEffect(() => {
    const checkDevTools = () => {
      const threshold = 160;
      const wDiff = window.outerWidth - window.innerWidth;
      const hDiff = window.outerHeight - window.innerHeight;
      if (wDiff > threshold || hDiff > threshold) {
        logViolation('DEVTOOLS_OPEN');
        return;
      }
      try {
        const el = document.createElement('div');
        Object.defineProperty(el, 'id', { get: () => { logViolation('DEVTOOLS_OPEN'); return ''; } });
        console.log('%c', el);
      } catch {}
    };
    const interval = setInterval(checkDevTools, 2000);
    return () => clearInterval(interval);
  }, [logViolation]);

  // Force heartbeat on page change to save answers immediately
  const pageChangeRef = useRef(currentPage);
  useEffect(() => {
    if (pageChangeRef.current !== currentPage) {
      pageChangeRef.current = currentPage;
      forceHeartbeat();
    }
  }, [currentPage, forceHeartbeat]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const examData = await fetchExam(examId);
        if (cancelled) return;

        if (!examData || !examData.questions) {
          setBlockMessage('Could not load exam. Link might be invalid.');
          return;
        }

        const dup = await checkDuplicate(examId, studentName);
        if (cancelled) return;

        if (dup && dup.isDuplicate) {
          setBlockMessage('You have already submitted this exam.');
          return;
        }

        const attemptRes = await startExamAttempt({
          examId,
          studentName,
          videoSessionId: Math.random().toString(36).substring(2) + Date.now().toString(36),
          preExamSessionId: preExamSessionId || ''
        });
        if (cancelled) return;

        if (attemptRes?.blocked) {
          setBlockMessage(attemptRes.reason === 'ALREADY_SUBMITTED'
            ? 'You have already submitted this exam.'
            : 'Could not start exam attempt.');
          return;
        }

        setExam(examData);
        recordEvent(examId, studentName, 'EXAM_LOADED', { title: examData.title });
      } catch (err) {
        if (cancelled) return;
        console.error('[ExamPortal Init Error]', err);
        setBlockMessage('Could not load exam. The server may be unavailable.');
      }
    };
    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId, studentName, preExamSessionId]);

  const noFaceCheckRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const faceBlockTimeoutRef = useRef(null);
  useEffect(() => {
    if (submitted) return;
    // never auto-submit during the pre-exam face verification screen
    if (!faceVerified) {
      setShowNoFaceWarning(false);
      setFaceBlocked(false);
      clearTimeout(noFaceCheckRef.current); noFaceCheckRef.current = null;
      clearTimeout(warningTimeoutRef.current); warningTimeoutRef.current = null;
      clearTimeout(faceBlockTimeoutRef.current); faceBlockTimeoutRef.current = null;
      return;
    }
    if (!faceDetected) {
      if (!warningTimeoutRef.current) {
        warningTimeoutRef.current = setTimeout(() => setShowNoFaceWarning(true), 1500);
      }
      if (!noFaceCheckRef.current) {
        noFaceCheckRef.current = setTimeout(() => {
          logViolation('AUTO_CLOSE: FACE_ABSENT_TOO_LONG');
          if (handleSubmitRef.current) handleSubmitRef.current(true);
        }, MAX_CONSECUTIVE_NO_FACE * 800);
      }
      if (!faceBlockTimeoutRef.current) {
        faceBlockTimeoutRef.current = setTimeout(() => setFaceBlocked(true), 2000);
      }
    } else {
      setShowNoFaceWarning(false);
      setFaceBlocked(false);
      clearTimeout(noFaceCheckRef.current); noFaceCheckRef.current = null;
      clearTimeout(warningTimeoutRef.current); warningTimeoutRef.current = null;
      clearTimeout(faceBlockTimeoutRef.current); faceBlockTimeoutRef.current = null;
    }
    return () => {
      clearTimeout(noFaceCheckRef.current);
      clearTimeout(warningTimeoutRef.current);
      clearTimeout(faceBlockTimeoutRef.current);
    };
  }, [faceDetected, submitted, logViolation, faceVerified]);

  const buildAnswersPayload = useCallback(() => {
    if (!exam) return [];
    return exam.questions.map((q, idx) => {
      const selectedIdx = answersRef.current[idx];
      return {
        questionIndex: idx, questionText: q.text, options: q.options,
        selectedOptionIndex: selectedIdx !== undefined ? selectedIdx : null,
        selectedOptionText: selectedIdx !== undefined ? q.options[selectedIdx] : null,
        correctOptionIndex: q.correct !== undefined ? q.correct : null,
        correctOptionText: q.correct !== undefined ? q.options[q.correct] : null,
        isCorrect: selectedIdx !== undefined && q.correct !== undefined ? selectedIdx === q.correct : null,
      };
    });
  }, [exam]);

  const handleSubmit = useCallback(async (autoSubmit = false) => {
    if (submittedRef.current || submittingRef.current || !exam || !faceVerified) return;

    const answeredCount = Object.keys(answersRef.current).length;
    const totalCount = exam.questions.length;
    if (!autoSubmit && answeredCount < totalCount) return;

    submittingRef.current = true;
    submittedRef.current = true;
    setSubmitted(true);
    setSubmitting(true);
    setSubmitError(null);
    setShowConfirm(false);
    stopWebcam();
    timer.stop();

    try {
      // drain the video upload queue first so recording is complete
      await getUploadQueue();

      const formData = new FormData();
      formData.append('examId', exam._id);
      formData.append('studentName', studentName);
      formData.append('uploadSessionId', uploadSessionId);
      formData.append('preExamSessionId', preExamSessionId || '');
      formData.append('answers', JSON.stringify(buildAnswersPayload()));
      formData.append('violations', JSON.stringify(violationsRef.current));

      await submitExam(formData);

      recordExamSubmit(examId, studentName);
      flushLogs();

      setSubmitting(false);
      setTimeout(() => {
        window.close();
        if (!window.closed) window.location.href = '/';
      }, 1500);
    } catch (e) {
      console.error('[ExamPortal Submit Error]', e);
      // revert to the exam so the student can retry without losing answers
      submittingRef.current = false;
      submittedRef.current = false;
      setSubmitted(false);
      setSubmitting(false);
      setSubmitError('Submission failed. Please try again.');
      if (timer.timeLeft > 0) timer.reset(timer.timeLeft);
    }
  }, [exam, studentName, uploadSessionId, preExamSessionId, faceVerified, stopWebcam, timer, buildAnswersPayload, examId, getUploadQueue, violationsRef]);

  handleSubmitRef.current = handleSubmit;

  const handleAnswerChange = useCallback((qIdx, optIdx, questionText) => {
    if (submittedRef.current) return;
    const newAnswers = { ...answersRef.current, [qIdx]: optIdx };
    answersRef.current = newAnswers;
    setAnswers(newAnswers);
    recordAnswerChange(examId, studentName, qIdx, optIdx, questionText);
  }, [examId, studentName]);

  const handleStartExam = useCallback(() => {
    if (!exam || !faceReady || submittedRef.current) return;
    setFaceVerified(true);
    setFaceReady(false);
    timer.reset(exam.duration * 60);
    recordEvent(examId, studentName, 'EXAM_STARTED', { title: exam.title });
  }, [exam, faceReady, timer, examId, studentName]);

  const totalPages = exam ? Math.ceil(exam.questions.length / QUESTIONS_PER_PAGE) : 1;
  const startIndex = (currentPage - 1) * QUESTIONS_PER_PAGE;
  const currentQuestions = exam ? exam.questions.slice(startIndex, startIndex + QUESTIONS_PER_PAGE) : [];

  const goToPage = useCallback((page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    requestAnimationFrame(() => {
      questionPanelRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
    });
  }, [totalPages]);

  if (submitted) {
    return (
      <div style={styles.submittedOverlay}>
        <div style={{ textAlign: 'center' }}>
          <div style={styles.checkCircle}>
            {submitting ? <div style={styles.spinnerSmall} /> : '✓'}
          </div>
          <h1 style={{ margin: '0 0 12px', fontWeight: 800 }}>
            {submitting ? 'Submitting...' : 'Exam Submitted'}
          </h1>
          {submitting ? (
            <p style={{ color: '#888', fontSize: 16 }}>Please wait...</p>
          ) : (
            <div>
              <p style={{ color: '#888', fontSize: 16 }}>Closing this window shortly...</p>
              <div style={styles.progressBar}><div style={styles.progressFill} /></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (blockMessage) {
    return (
      <div style={styles.blockedOverlay}>
        <div style={styles.blockedCard}>
          <div style={styles.blockedIcon}>⛔</div>
          <h2 style={{ margin: '0 0 8px', fontWeight: 800, color: '#c62828' }}>Access Denied</h2>
          <p style={{ color: '#888', fontSize: 15, margin: '0 0 24px' }}>{blockMessage}</p>
          <button onClick={() => window.location.href = '/'} style={styles.homeBtn}
            onMouseEnter={e => e.currentTarget.style.background = '#1b5e20'}
            onMouseLeave={e => e.currentTarget.style.background = '#2e7d32'}
          >Go Home</button>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner} />
        <p style={{ color: '#888' }}>Loading Exam...</p>
      </div>
    );
  }

  // ── FACE VERIFICATION / CAMERA ERROR SCREEN ──
  if (!faceVerified) {
    return (
      <div style={styles.faceVerifyOverlay}>
        <div style={styles.faceVerifyCard}>
          <div style={styles.facePreviewWrap}>
            <video ref={videoRef} autoPlay muted playsInline style={styles.facePreviewVideo} />
            <canvas ref={canvasRef} style={styles.facePreviewCanvas} />
          </div>

          {camError ? (
            <div>
              <p style={{ color: '#ff5252', fontSize: 15, fontWeight: 700, marginBottom: 8 }}>⚠️ {camError}</p>
              <button onClick={retryWebcam} style={styles.retryBtn}
                onMouseEnter={e => e.currentTarget.style.background = '#e65100'}
                onMouseLeave={e => e.currentTarget.style.background = '#f57c00'}
              >🔄 Retry Camera Access</button>
            </div>
          ) : !recording ? (
            <p style={{ color: '#ff9800', fontSize: 14, fontWeight: 600 }}>⏳ Requesting camera access...</p>
          ) : !faceDetected ? (
            <div>
              <p style={{ color: '#ff5252', fontSize: 16, fontWeight: 700 }}>⚠️ FACE NOT FOUND</p>
              <p style={{ color: '#888', fontSize: 13 }}>Please position your face in the camera frame</p>
            </div>
          ) : !faceReady ? (
            <div>
              <p style={{ color: '#4cbb4c', fontSize: 16, fontWeight: 700 }}>✅ Face Detected</p>
              <p style={{ color: '#888', fontSize: 13, marginBottom: 8 }}>Verifying identity — keep still...</p>
              <div style={styles.miniSpinner} />
            </div>
          ) : (
            <div>
              <p style={{ color: '#4cbb4c', fontSize: 16, fontWeight: 700 }}>✅ Face Verified</p>
              <p style={{ color: '#888', fontSize: 13, marginBottom: 20 }}>Your identity has been confirmed.</p>
              <button onClick={handleStartExam} style={styles.startExamBtn}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >Start Exam →</button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const answeredCount = Object.keys(answers).length;
  const totalCount = exam.questions.length;
  const timerWarning = timer.timeLeft !== null && timer.timeLeft <= 60;
  const allAnswered = answeredCount === totalCount;

  return (
    <div style={styles.container} className="exam-content">
      <div style={styles.questionPanel} ref={questionPanelRef}>
        <div style={styles.header}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#111' }}>{exam.title}</h2>
          <div style={{ ...styles.timer, ...(timerWarning ? styles.timerWarning : styles.timerNormal) }}>
            <span style={{ marginRight: 4 }}>⏱</span>
            {timer.formatTime(timer.timeLeft)}
          </div>
        </div>

        {camError && (
          <div style={styles.camErrorBanner}>
            <span>⚠️ {camError}</span>
            <button onClick={retryWebcam} style={styles.retryCamBtn}>Retry</button>
          </div>
        )}

        {!recording && !camError && (
          <div style={styles.recordingWarning}>Recording inactive — check your camera/microphone</div>
        )}

        {!faceDetected && (
          <div style={{ ...styles.noFaceBanner, animation: showNoFaceWarning ? 'pulse 0.8s ease-in-out infinite' : 'none' }}>
            ⚠️ FACE NOT FOUND — Your face must be visible at all times during the exam
          </div>
        )}

        {faceDetected && !eyesVisible && (
          <div style={styles.eyesWarning}>👁️ EYES NOT VISIBLE — Keep both eyes open and visible on the camera</div>
        )}

        {gazeAway && (
          <div style={styles.gazeWarning}>👀 Look at the screen — Your eyes must be on the exam</div>
        )}

        <div style={styles.progressSection}>
          <div style={styles.progressLabel}>
            <span style={{ color: '#888', fontSize: 13 }}>Progress</span>
            <span style={{ color: '#4cbb4c', fontSize: 13, fontWeight: 700 }}>{answeredCount} / {totalCount} answered</span>
          </div>
          <div style={styles.progressTrack}>
            <div style={{
              ...styles.progressThumb, width: `${(answeredCount / totalCount) * 100}%`,
              background: allAnswered ? 'linear-gradient(90deg,#4cbb4c,#2e9e2e)' : 'linear-gradient(90deg,#ff9800,#f57c00)'
            }} />
          </div>
        </div>

        <hr style={styles.divider} />

        {/* Pagination navigation - top */}
        {totalPages > 1 && (
          <div style={styles.paginationBar}>
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
              style={{ ...styles.pageBtn, opacity: currentPage === 1 ? 0.4 : 1 }}>
              ‹ Prev
            </button>
            <div style={styles.pageNumbers}>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => goToPage(p)}
                  style={{
                    ...styles.pageNum,
                    background: p === currentPage ? '#4cbb4c' : '#f0f0f0',
                    color: p === currentPage ? '#fff' : '#555',
                    fontWeight: p === currentPage ? 700 : 400
                  }}>{p}</button>
              ))}
            </div>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
              style={{ ...styles.pageBtn, opacity: currentPage === totalPages ? 0.4 : 1 }}>
              Next ›
            </button>
          </div>
        )}

        {/* Watermark overlay */}
        <div style={styles.watermark}>{studentName} · {exam._id?.slice(-6)}</div>

        {faceBlocked && (
          <div style={styles.faceBlockOverlay}>
            <div style={styles.faceBlockContent}>
              <div style={styles.faceBlockIcon}>👤</div>
              <h2 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 800, color: '#fff' }}>No Face Detected</h2>
              <p style={{ margin: '0 0 8px', color: '#ffcc00', fontSize: 16, fontWeight: 600 }}>
                ⚠️ Your face is not visible in the webcam
              </p>
              <p style={{ margin: 0, color: '#ccc', fontSize: 14 }}>
                Please position your face in the camera frame to continue the exam.
                The webcam recording remains active.
              </p>
            </div>
          </div>
        )}

        {currentQuestions.map((q, idx) => {
          const realIdx = startIndex + idx;
          const selected = answers[realIdx];
          return (
            <div key={realIdx} style={{ ...styles.questionCard, borderColor: selected !== undefined ? '#4cbb4c' : '#eee', background: selected !== undefined ? '#f8fdf8' : '#fff' }}>
              <div style={styles.questionHeader}>
                <span style={{ ...styles.qNumber, background: selected !== undefined ? '#4cbb4c' : '#f5f5f5', color: selected !== undefined ? '#fff' : '#999' }}>{realIdx + 1}</span>
                <h4 style={styles.questionText}>{q.text}</h4>
              </div>
              <div style={styles.optionsContainer}>
                {q.options.map((opt, optIdx) => {
                  const isSelected = selected === optIdx;
                  return (
                    <label key={optIdx} style={{ ...styles.option, borderColor: isSelected ? '#4cbb4c' : '#eee', background: isSelected ? '#e8f5e9' : '#fafafa', color: isSelected ? '#1b5e20' : '#444', fontWeight: isSelected ? 600 : 400 }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#ccc'; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = '#eee'; }}
                    >
                      <span style={{ ...styles.radio, borderColor: isSelected ? '#4cbb4c' : '#ccc', background: isSelected ? '#4cbb4c' : 'transparent' }}>
                        {isSelected && <span style={styles.radioInner} />}
                      </span>
                      <input type="radio" name={`q-${realIdx}`} value={optIdx}
                        onChange={() => handleAnswerChange(realIdx, optIdx, q.text)} style={{ display: 'none' }} />
                      {opt}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Pagination navigation - bottom */}
        {totalPages > 1 && (
          <div style={styles.paginationBar}>
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
              style={{ ...styles.pageBtn, opacity: currentPage === 1 ? 0.4 : 1 }}>
              ‹ Prev
            </button>
            <div style={styles.pageIndicator}>
              Page {currentPage} of {totalPages}
            </div>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
              style={{ ...styles.pageBtn, opacity: currentPage === totalPages ? 0.4 : 1 }}>
              Next ›
            </button>
          </div>
        )}

        {!allAnswered && (
          <div style={styles.unansweredWarning}>
            ⚠️ You must answer all {totalCount} questions before submitting ({totalCount - answeredCount} remaining)
          </div>
        )}

        {submitError && (
          <div style={styles.submitErrorBanner}>
            <span>⚠️ {submitError}</span>
            <button onClick={() => handleSubmit(false)} style={styles.retrySubmitBtn}>Retry Submission</button>
          </div>
        )}

        <div style={styles.submitSection}>
          <button onClick={() => allAnswered && !submitting && setShowConfirm(true)} disabled={!allAnswered || submitting}
            style={{ ...styles.submitBtn, background: !allAnswered ? '#999' : '#d32f2f', cursor: !allAnswered ? 'not-allowed' : 'pointer', opacity: !allAnswered ? 0.6 : 1 }}
            onMouseEnter={e => { if (allAnswered) e.currentTarget.style.background = '#b71c1c'; }}
            onMouseLeave={e => { if (allAnswered) e.currentTarget.style.background = '#d32f2f'; }}
          >
            {!allAnswered ? `Answer all questions (${totalCount - answeredCount} remaining)` : 'Submit Exam'}
          </button>
        </div>
      </div>

      <div style={styles.proctorPanel}>
        <div style={styles.proctorHeader}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{recording ? '🔴' : '⭕'} LIVE PROCTORING</h3>
          <span style={{ fontSize: 12, color: recording ? '#ff4c4c' : '#888', fontWeight: 600 }}>
            {recording ? '🎙️ REC' : '⏸️ PAUSED'}
          </span>
        </div>

        <div style={styles.webcamContainer}>
          <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, background: faceDetected ? 'rgba(76,187,76,.8)' : 'rgba(255,50,50,.8)', color: '#fff' }}>
            {faceDetected ? '👤 FACE OK' : '⚠️ FACE NOT FOUND'}
          </div>
          {gazeAway && (
            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10, padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: 'rgba(255,200,0,.8)', color: '#000' }}>
              👀 LOOK AWAY
            </div>
          )}
          <video ref={videoRef} autoPlay muted playsInline style={styles.video} />
          <canvas ref={canvasRef} style={styles.canvasOverlay} />
          <div style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 10, padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: eyesVisible ? 'rgba(76,187,76,.8)' : 'rgba(255,50,50,.85)', color: '#fff' }}>
            {eyesVisible ? '👁️ EYES OK' : '👁️ EYES COVERED / CLOSED'}
          </div>
        </div>

        <div style={styles.statsGrid}>
          <div style={styles.statBox}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Violations</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: violations.length > 0 ? '#ff4c4c' : '#4cbb4c' }}>{violations.length}</div>
          </div>
          <div style={styles.statBox}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>Answered</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#4cbb4c' }}>{answeredCount}/{totalCount}</div>
          </div>
        </div>

        <div style={{ ...styles.timer, ...(timerWarning ? styles.timerWarning : styles.timerNormal), marginBottom: 12 }}>
          Time Left: ⏱ {timer.formatTime(timer.timeLeft)}
        </div>

        <div style={styles.violationLogSection}>
          <h4 style={styles.violationLogHeader}>Violation Log</h4>
          <div style={styles.violationLogList}>
            {violations.length === 0 && <span style={{ color: '#555', fontSize: 13 }}>No violations yet...</span>}
            {violations.map((v, i) => (
              <div key={i} style={styles.violationItem}>
                <div style={{ color: '#ff4c4c', fontSize: 13, fontWeight: 600 }}>⚠️ {v.type}</div>
                <div style={{ color: '#666', fontSize: 11, marginTop: 2 }}>{new Date(v.timestamp).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Submit confirmation modal */}
      {showConfirm && (
        <div style={styles.confirmOverlay}>
          <div style={styles.confirmCard}>
            <div style={styles.confirmIcon}>📤</div>
            <h2 style={{ margin: '0 0 8px', fontSize: 22, fontWeight: 800, color: '#111' }}>Submit Exam?</h2>
            <p style={{ margin: '0 0 24px', color: '#666', fontSize: 14, lineHeight: 1.6 }}>
              You have answered all {totalCount} questions. Once submitted you cannot re-open or change your answers.
            </p>
            <div style={styles.confirmActions}>
              <button onClick={() => setShowConfirm(false)} style={styles.cancelBtn}
                onMouseEnter={e => e.currentTarget.style.background = '#f0f0f0'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >Cancel</button>
              <button onClick={() => handleSubmit(false)} style={styles.confirmBtn}
                onMouseEnter={e => e.currentTarget.style.background = '#b71c1c'}
                onMouseLeave={e => e.currentTarget.style.background = '#d32f2f'}
              >Confirm & Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { display: 'flex', height: '100vh', background: '#f0f0f0', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
  questionPanel: { flex: 1, overflowY: 'auto', padding: '40px', background: 'white', position: 'relative' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 12 },
  timer: { padding: '8px 18px', borderRadius: 8, fontWeight: 700, fontSize: 20, fontVariantNumeric: 'tabular-nums' },
  timerNormal: { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' },
  timerWarning: { background: '#fdecea', color: '#d32f2f', border: '1px solid #f5c6cb' },
  recordingWarning: { padding: '10px 16px', background: '#fff3e0', borderRadius: 8, color: '#e65100', fontSize: 13, fontWeight: 600, marginBottom: 16, border: '1px solid #ffe0b2' },
  noFaceBanner: { padding: '14px 20px', background: '#d32f2f', color: '#fff', borderRadius: 8, fontSize: 15, fontWeight: 700, marginBottom: 16, textAlign: 'center', border: '2px solid #b71c1c' },
  eyesWarning: { padding: '12px 16px', background: '#fdecea', borderRadius: 8, color: '#b71c1c', fontSize: 13, fontWeight: 700, marginBottom: 16, border: '1px solid #f5c6cb', animation: 'pulse 1s ease-in-out infinite' },
  gazeWarning: { padding: '10px 16px', background: '#fff8e1', borderRadius: 8, color: '#f57f17', fontSize: 13, fontWeight: 700, marginBottom: 16, border: '1px solid #ffe082', animation: 'pulse 1s ease-in-out infinite' },
  camErrorBanner: { padding: '12px 16px', background: '#fdecea', borderRadius: 8, color: '#c62828', fontSize: 13, fontWeight: 600, marginBottom: 16, border: '1px solid #ef9a9a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  retryCamBtn: { padding: '6px 16px', borderRadius: 6, border: 'none', background: '#c62828', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' },
  progressSection: { marginBottom: 32 },
  progressLabel: { display: 'flex', justifyContent: 'space-between', marginBottom: 6 },
  progressTrack: { height: 6, background: '#eee', borderRadius: 3, overflow: 'hidden' },
  progressThumb: { height: '100%', borderRadius: 3, transition: 'width .3s ease' },
  divider: { border: 'none', borderTop: '1px solid #eee', margin: '0 0 28px' },
  questionCard: { marginBottom: 28, padding: 24, border: '2px solid #eee', borderRadius: 12, transition: 'all .25s ease' },
  questionHeader: { display: 'flex', gap: 12, alignItems: 'flex-start' },
  qNumber: { minWidth: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800, flexShrink: 0 },
  questionText: { margin: '4px 0 16px', fontSize: 16, lineHeight: 1.5, color: '#222', fontWeight: 600 },
  optionsContainer: { paddingLeft: 44 },
  option: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 8, borderRadius: 10, border: '2px solid #eee', cursor: 'pointer', transition: 'all .2s ease', fontSize: 15 },
  radio: { width: 20, height: 20, borderRadius: '50%', flexShrink: 0, border: '2px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .2s' },
  radioInner: { width: 8, height: 8, borderRadius: '50%', background: '#fff' },
  submitSection: { paddingTop: 12, paddingBottom: 40 },
  submitBtn: { padding: '16px 40px', color: '#fff', fontSize: 16, fontWeight: 700, borderRadius: 10, border: 'none', transition: 'all .25s' },
  unansweredWarning: { padding: '10px 16px', background: '#fff3e0', borderRadius: 8, color: '#e65100', fontSize: 13, fontWeight: 600, marginBottom: 16, border: '1px solid #ffe0b2' },
  submitErrorBanner: { padding: '12px 16px', background: '#fdecea', borderRadius: 8, color: '#c62828', fontSize: 13, fontWeight: 600, marginBottom: 16, border: '1px solid #ef9a9a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  retrySubmitBtn: { padding: '6px 16px', borderRadius: 6, border: 'none', background: '#c62828', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' },
  proctorPanel: { width: 380, background: '#1a1a1a', color: 'white', padding: 16, display: 'flex', flexDirection: 'column', flexShrink: 0 },
  proctorHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #333' },
  webcamContainer: { position: 'relative', width: '100%', backgroundColor: '#000', borderRadius: 10, overflow: 'hidden', marginBottom: 12 },
  video: { width: '100%', display: 'block' },
  canvasOverlay: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' },
  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 },
  statBox: { padding: '10px 12px', background: '#222', borderRadius: 8, textAlign: 'center' },
  violationLogSection: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
  violationLogHeader: { margin: '0 0 8px 0', fontSize: 13, fontWeight: 700, borderBottom: '1px solid #333', paddingBottom: 8 },
  violationLogList: { flex: 1, overflowY: 'auto', background: '#222', padding: 10, borderRadius: 8, minHeight: 0 },
  violationItem: { marginBottom: 6, padding: '8px 10px', background: '#2a1a1a', borderRadius: 6, borderLeft: '3px solid #ff4c4c' },
  submittedOverlay: { height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0a0a0a, #111827)', color: '#fff', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" },
  checkCircle: { width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36, margin: '0 auto 24px' },
  progressBar: { width: 200, height: 3, background: '#222', borderRadius: 3, margin: '24px auto 0', overflow: 'hidden' },
  progressFill: { width: '100%', height: '100%', background: '#4cbb4c', borderRadius: 3, animation: 'shrink 1.5s ease-in forwards' },
  spinner: { width: 40, height: 40, border: '4px solid #eee', borderTopColor: '#4cbb4c', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' },
  spinnerSmall: { width: 36, height: 36, border: '3px solid rgba(255,255,255,.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  miniSpinner: { width: 26, height: 26, border: '3px solid #333', borderTopColor: '#4cbb4c', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' },
  loadingContainer: { padding: '50px', textAlign: 'center', fontFamily: "'Segoe UI', sans-serif" },
  blockedOverlay: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5', fontFamily: "'Segoe UI', sans-serif", padding: 24 },
  blockedCard: { background: '#fff', borderRadius: 20, padding: '48px 40px', maxWidth: 420, width: '100%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,.1)' },
  blockedIcon: { fontSize: 56, marginBottom: 16 },
  homeBtn: { padding: '12px 32px', borderRadius: 10, border: 'none', background: '#2e7d32', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', transition: 'all .25s' },
  faceVerifyOverlay: { height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a', fontFamily: "'Segoe UI', sans-serif", padding: 24 },
  faceVerifyCard: { background: '#1a1a1a', borderRadius: 20, padding: '40px', maxWidth: 480, width: '100%', textAlign: 'center', border: '1px solid #333' },
  facePreviewWrap: { width: 160, height: 120, margin: '0 auto 20px', borderRadius: 12, overflow: 'hidden', background: '#000', position: 'relative' },
  facePreviewVideo: { width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' },
  facePreviewCanvas: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' },
  startExamBtn: { padding: '12px 36px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg,#4cbb4c,#2e9e2e)', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer', transition: 'all .25s' },
  retryBtn: { padding: '12px 24px', borderRadius: 10, border: 'none', background: '#f57c00', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer', transition: 'all .25s', marginTop: 12 },
  paginationBar: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 20, padding: '8px 0' },
  pageBtn: { padding: '8px 18px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', color: '#333', fontWeight: 600, fontSize: 14, cursor: 'pointer', transition: 'all .2s' },
  pageNumbers: { display: 'flex', gap: 6, alignItems: 'center' },
  pageNum: { width: 34, height: 34, borderRadius: 8, border: 'none', fontSize: 14, cursor: 'pointer', transition: 'all .2s', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  pageIndicator: { color: '#888', fontSize: 14, fontWeight: 600 },
  watermark: { position: 'fixed', bottom: 8, right: 8, zIndex: 999, fontSize: 11, color: 'rgba(0,0,0,0.12)', fontWeight: 700, pointerEvents: 'none', userSelect: 'none', fontFamily: 'monospace', letterSpacing: 0.5 },
  faceBlockOverlay: {
    position: 'absolute', inset: 0, zIndex: 100,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: 0
  },
  faceBlockContent: {
    textAlign: 'center', maxWidth: 420, padding: 40
  },
  faceBlockIcon: {
    fontSize: 72, marginBottom: 16,
    width: 120, height: 120, borderRadius: '50%',
    background: 'rgba(255,50,50,0.2)', border: '3px solid #ff4c4c',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 24px'
  },
  confirmOverlay: {
    position: 'fixed', inset: 0, zIndex: 200,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24
  },
  confirmCard: {
    background: '#fff', borderRadius: 20, padding: '40px 36px',
    maxWidth: 440, width: '100%', textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0,0,0,.35)'
  },
  confirmIcon: { fontSize: 44, marginBottom: 12 },
  confirmActions: { display: 'flex', gap: 12, marginTop: 8 },
  cancelBtn: {
    flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #ddd',
    background: '#fff', color: '#555', fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'all .2s'
  },
  confirmBtn: {
    flex: 1, padding: '12px', borderRadius: 10, border: 'none',
    background: '#d32f2f', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', transition: 'all .2s'
  }
};

export default ExamPortal;